import {
  FileSpreadsheet,
} from "lucide-react";
import {
  ModuleHeader,
} from "../../shared/components/ModuleHeader";
import {
  TableState,
} from "../../shared/components/TableState";
import { useReportsWorkspace, type ReportsModuleProps } from "./useReportsWorkspace";

const reportStatusLabels: Record<string, string> = {
  DRAFT: "Taslak",
  SUBMITTED: "Satın Almaya iletildi",
  IN_PROCUREMENT: "Teklif sürecinde",
  READY_FOR_APPROVAL: "Onaya hazır",
  PENDING_APPROVAL: "Onay bekliyor",
  REVISION_REQUESTED: "Düzeltme bekleniyor",
  APPROVED: "Onaylandı",
  REJECTED: "Reddedildi",
  Taslak: "Taslak",
  "Satın Almaya iletildi": "Satın Almaya iletildi",
  "Teklif toplanıyor": "Teklif sürecinde",
  "Onaya gönderilmeye hazır": "Onaya hazır",
  "Onay bekliyor": "Onay bekliyor",
  Revizyon: "Revizyon bekleniyor",
  "Düzeltme istendi": "Düzeltme bekleniyor",
  Onaylandı: "Onaylandı",
  Reddedildi: "Reddedildi",
};

function reportStatusTone(status: string) {
  if (["APPROVED", "Onaylandı"].includes(status)) return "success";
  if (
    [
      "REJECTED",
      "Reddedildi",
      "REVISION_REQUESTED",
      "Revizyon",
      "Düzeltme istendi",
    ].includes(status)
  ) {
    return "danger";
  }
  if (
    [
      "IN_PROCUREMENT",
      "READY_FOR_APPROVAL",
      "PENDING_APPROVAL",
      "Teklif toplanıyor",
      "Onaya gönderilmeye hazır",
      "Onay bekliyor",
    ].includes(status)
  ) {
    return "warning";
  }
  return "info";
}

export function ReportsModule(props: ReportsModuleProps) {
  const {
    report,
    loading,
    exportReport,
    approvedVolume,
  } = useReportsWorkspace(props);
  return (
    <>
      <ModuleHeader
        eyebrow="Yönetim özeti"
        title="Satın Alma Raporları"
        description="Gerçekleşen satın almaları ve karar sürecinin güncel durumunu tek bakışta inceleyin."
        action={
          <button
            className="secondary-button"
            disabled={!report}
            onClick={exportReport}
          >
            <FileSpreadsheet size={15} /> Dışa aktar
          </button>
        }
      />
      {loading ? (
        <TableState
          title="Rapor hazırlanıyor"
          description="Kurumsal kayıtlar doğrulanarak özetleniyor."
        />
      ) : report ? (
        <>
          <section className="report-metrics">
            <article>
              <small>Sonuçlanan talep</small>
              <strong>{report.completedRequests}</strong>
              <span>Onaylanan ve reddedilen</span>
            </article>
            <article>
              <small>Ortalama teklif</small>
              <strong>{report.averageOffers.toFixed(1)}</strong>
              <span>Talep başına</span>
            </article>
            <article>
              <small>Ortalama onay süresi</small>
              <strong>
                {report.averageApprovalDays === null
                  ? "—"
                  : `${report.averageApprovalDays.toFixed(1)} gün`}
              </strong>
              <span>Tamamlanan kayıtlardan</span>
            </article>
            <article>
              <small>Onaylı satın alma</small>
              <strong>{approvedVolume}</strong>
              <span>Seçilen teklif tutarlarından</span>
            </article>
          </section>
          <div className="report-overview">
            <section className="module-card report-panel">
              <div className="section-heading compact">
                <div>
                  <span className="section-kicker">Gerçekleşen hacim</span>
                  <h2>Onaylanan satın almalar</h2>
                </div>
              </div>
              {report.volumes.length ? (
                <div className="report-data-list">
                  {report.volumes.map((item) => (
                    <article key={item.currency}>
                      <div>
                        <strong>{item.currency}</strong>
                        <small>{item.request_count} talep</small>
                      </div>
                      <strong>{item.amount}</strong>
                    </article>
                  ))}
                </div>
              ) : (
                <TableState
                  title="Onaylı hacim oluşmadı"
                  description="Onaylanan satın alma talebi bulunmuyor."
                />
              )}
            </section>
            <section className="module-card report-panel">
              <div className="section-heading compact">
                <div>
                  <span className="section-kicker">Güncel iş yükü</span>
                  <h2>Süreçteki talepler</h2>
                </div>
              </div>
              <div className="report-status-list">
                {report.statuses.map((item) => (
                  <article
                    className={`report-status report-status-${reportStatusTone(item.status)}`}
                    key={item.status}
                  >
                    <span />
                    <strong>
                      {reportStatusLabels[item.status] ?? item.status}
                    </strong>
                    <strong>{item.count}</strong>
                  </article>
                ))}
              </div>
            </section>
          </div>
          {report.categories.length ? (
            <section className="module-card report-panel report-category-panel">
              <div className="section-heading compact">
                <div>
                  <span className="section-kicker">Kategori dağılımı</span>
                  <h2>Satın alma alanları</h2>
                </div>
              </div>
              <div className="report-category-list">
                {report.categories.map((item) => (
                  <article key={`${item.category}-${item.currency}`}>
                    <div>
                      <strong>{item.category}</strong>
                      <small>
                        {item.request_count} onaylı talep · {item.currency}
                      </small>
                    </div>
                    <strong>{item.amount}</strong>
                  </article>
                ))}
              </div>
            </section>
          ) : null}
        </>
      ) : (
        <TableState
          title="Rapor verisi alınamadı"
          description="Bağlantıyı kontrol ederek yeniden deneyin."
        />
      )}
    </>
  );
}
